import { useEffect, useRef } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import { getHMTenantSelection } from '@/modules/hiring-manager/utils/tenant';
import { useCurrentUserQuery } from '@/modules/users/queries/useCurrentUserQuery';
import { actionErrorMessage } from '@/utils/actionErrorMessage';

import type { NewChatLocationState } from './useNewChat';
import { useStartChatMutation } from './useStartChatMutation';

/**
 * Sends a new chat's first message from the chat's own page, which `useNewChat` opened before the chat existed. It is
 * sent once: the navigation forgets it, so going back to the page or reloading it opens the chat instead. finlay-api
 * refuses a second start with the same ID anyway, so it can never make two chats.
 */
export const useChatStartOnOpen = (chatId: string) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const currentUserQuery = useCurrentUserQuery();
  const { selectedTenantId } = getHMTenantSelection(
    currentUserQuery.data,
    searchParams,
  );
  const { mutate, isPending, isError, variables, error } =
    useStartChatMutation();
  const start = (location.state as NewChatLocationState | null)?.start;
  // React runs effects twice in development; the first message mustn't be sent twice.
  const startedFor = useRef<string | null>(null);

  useEffect(() => {
    if (!start || !selectedTenantId || startedFor.current === chatId) return;
    startedFor.current = chatId;
    mutate({
      id: chatId,
      tenantId: selectedTenantId,
      text: start.text,
      contextJobId: start.contextJobId,
    });
    navigate(
      { pathname: location.pathname, search: location.search },
      { replace: true, state: null },
    );
  }, [
    chatId,
    start,
    selectedTenantId,
    mutate,
    navigate,
    location.pathname,
    location.search,
  ]);

  return {
    /** The first message while it's sent and answered: the chat shows it with a pending reply. */
    starting: isPending ? variables.text : (start?.text ?? null),
    /** The first message, if sending it failed: when the chat doesn't exist either, it goes back in the box. */
    failure:
      isError && variables
        ? {
            text: variables.text,
            contextJobId: variables.contextJobId ?? undefined,
            message: actionErrorMessage('start the chat', error),
          }
        : null,
  };
};
